import {PAYMENT_KEYS} from "@/api/paymentApi";
import type {PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {INFINITY_THRESHOLD} from "@/store/constants";
import {create} from "@/store/createStore";
import type {
  TCurrency,
  TNonFunctionKeys,
  TPackageCategory,
  TPackageName,
  TPackagePaymentPeriodType,
  TSubscriptionWithDiscountOptions,
} from "@/store/types";
import dayjs from "dayjs";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TPackageInformation = Omit<TSubscriptionWithDiscountOptions, "name"> & {
  name?: TPackageName;
  price?: number;
};

type TState = {
  originalPackage: TPackageInformation;
  modifiedPackage: TPackageInformation;
  changePackage(
    packageType: TPackageName,
    packages: TSubscriptionWithDiscountOptions[],
    subscription: PackageSubscriptionSerializer | null,
  ): void;
  setInitialPackage(
    packages: TSubscriptionWithDiscountOptions[],
    subscription: PackageSubscriptionSerializer | null,
  ): void;
  changeValue<K extends NonNullable<TNonFunctionKeys<TPackageInformation>>>(
    key: K,
    value: TPackageInformation[K],
  ): void;
  cancelChanges(): void;
  saveChanges(
    agency_id: number,
    isAgency: boolean,
    selectedCurrency: TCurrency,
    subscription: PackageSubscriptionSerializer | null,
  ): Promise<void>;
  customizePackage(isAgency?: boolean): void;
  cancelSubscription(agency_id: number, cancel_now: boolean, cancellation_reason?: string): Promise<void>;
  applyPackageList(packages: TSubscriptionWithDiscountOptions[]): void;
  changeCurrency(packages: TSubscriptionWithDiscountOptions[]): void;
  reset(): void;
  setAdminAssignedTrial(enabled: boolean, packages: TSubscriptionWithDiscountOptions[]): void;
};

const initialPackageState = {
  id: 0,
  name: "NONE",
  price_per_year: 0,
  price_per_month: 0,
  currency: "CHF",
  influencers_per_campaign: 0,
  invitations_per_campaign: 0,
  buyout: 0,
  influencer_level_insights: false,
  multiple_active_campaigns: false,
  multiple_brands: false,
  other_countries: false,
  package_type: "MONTHLY",
  priority_notifications_and_advertising: false,
  personal_account_manager: false,
  price_paid: 0,
  discount: 0,
  valid_from: dayjs().toISOString(),
  valid_until: dayjs().add(1, "month").toISOString(),
  buyout_used: 0,
  price_per_year_with_discount: 0,
  price_per_month_with_discount: 0,
  discount_percentage: 0,
  price: 0,
  subscription_email_sent: false,
  recurring: true,
  tiktok_campaigns: false,
  instagram_campaigns: false,
  package_category: "INSTAGRAM",
  number_of_campaigns: 0,
  number_of_seats: 0,
  extra_campaign_price: 0,
  extra_seat_price: 0,
  is_admin_assigned: false,
  campaign_booster: false,
  content_approval: false,
  personal_link_stickers: false,
  pro_creator_mode: false,
  short_term_campaigns: false,
  social_feed_filters: false,
  tiktok_visibility_country_count: 1,
  stripe_subscription_id: "",
  stripe_monthly_price_id: "",
  stripe_yearly_price_id: "",
  stripe_product_id: "",
  stripe_tax_id: "",
  access_to_influencer_network: false,
  agency: null,
  assigned_from_payment_link: false,
  campaigns_used: 0,
} as TPackageInformation;

const BILLING_PERIOD_DURATION_MONTHS: Record<TPackagePaymentPeriodType, number> = {
  HALF_YEARLY: 6,
  MONTHLY: 1,
  QUARTERLY: 3,
  THREE_PLUS_TWELVE: 3,
  YEARLY: 12,
};

export const ADMIN_ASSIGNED_TRIAL_DAYS = 14;

// An admin-assigned trial is always free and non-recurring. A new trial runs a fixed number of
// days from now; an existing one keeps its dates so that editing it never extends the trial.
const applyTrialValues = (draft: TPackageInformation, shouldResetDates: boolean) => {
  draft.admin_assigned_trial = true;
  draft.discount = 0;
  draft.price = 0;
  draft.price_per_month = 0;
  draft.price_per_month_with_discount = 0;
  draft.price_per_year = 0;
  draft.price_per_year_with_discount = 0;
  draft.recurring = false;
  if (shouldResetDates) {
    const now = dayjs();
    draft.valid_from = now.toISOString();
    draft.valid_until = now.add(ADMIN_ASSIGNED_TRIAL_DAYS, "day").toISOString();
  }
};

const getPricePayload = (packageType?: string, price?: number) =>
  packageType === "MONTHLY" || packageType === "YEARLY"
    ? {price_per_month: price, price_per_year: price}
    : {price_paid: price};

const getSubscriptionPrice = (sub: {
  package_type?: string;
  price_per_month?: number;
  price_per_year?: number;
  price_paid?: number;
}): number => {
  if (sub.package_type === "YEARLY") return sub.price_per_year ?? 0;
  if (sub.package_type === "MONTHLY") return sub.price_per_month ?? 0;
  // QUARTERLY / HALF_YEARLY / THREE_PLUS_TWELVE: the initial-period price is price_per_month
  // times the period length. Compute it from the (per-currency) monthly price unless an
  // existing subscription already carries an explicit price_paid (e.g. custom ENTERPRISE).
  const months = BILLING_PERIOD_DURATION_MONTHS[sub.package_type as TPackagePaymentPeriodType];
  if (months !== undefined && !sub.price_paid) return (sub.price_per_month ?? 0) * months;
  return sub.price_paid ?? 0;
};

// Package.buyout is a per-year count. Standard plans (monthly/yearly) store the yearly count;
// non-standard plans store the period share (yearly * period_months / 12). These months are the
// basis each stored value represents: monthly/yearly = the full year (12), quarterly/3+12 = 3,
// half-yearly = 6. null (unlimited) and the INFINITY_THRESHOLD sentinel are never scaled.
const BUYOUT_BASIS_MONTHS: Record<string, number> = {
  HALF_YEARLY: 6,
  MONTHLY: 12,
  QUARTERLY: 3,
  THREE_PLUS_TWELVE: 3,
  YEARLY: 12,
};

const scaleBuyoutForPeriod = (yearlyBuyout?: number | null, packageType?: string): number | null | undefined => {
  if (yearlyBuyout === null || yearlyBuyout === undefined || yearlyBuyout >= INFINITY_THRESHOLD) return yearlyBuyout;
  const months = packageType ? BUYOUT_BASIS_MONTHS[packageType] : undefined;
  if (months === undefined) return yearlyBuyout;
  return Math.round((yearlyBuyout * months) / 12);
};

const unscaleBuyoutToYearly = (storedBuyout?: number | null, packageType?: string): number | null | undefined => {
  if (storedBuyout === null || storedBuyout === undefined || storedBuyout >= INFINITY_THRESHOLD) return storedBuyout;
  const months = packageType ? BUYOUT_BASIS_MONTHS[packageType] : undefined;
  if (!months) return storedBuyout;
  return Math.round((storedBuyout * 12) / months);
};

const initialState: {[K in TNonFunctionKeys<TState>]: TState[K]} = {
  originalPackage: initialPackageState,
  modifiedPackage: initialPackageState,
};

export const getSubscriptionCategory = (
  subscription: Pick<PackageSubscriptionSerializer, "instagram_campaigns" | "tiktok_campaigns"> | null,
): TPackageCategory =>
  subscription?.instagram_campaigns && subscription?.tiktok_campaigns
    ? "BOTH"
    : subscription?.tiktok_campaigns
      ? "TIKTOK"
      : "INSTAGRAM";

const usePackageAdminStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        applyPackageList: packages => {
          const template = packages.find(p => p.name === get().modifiedPackage.name);
          const isCreating = get().originalPackage.name === "NONE";
          const isTrial = !!get().modifiedPackage.admin_assigned_trial;
          set(
            state => {
              if (!template) {
                if (packages.length > 0) state.modifiedPackage.currency = packages[0].currency;
                return;
              }

              // Landing back on the saved currency and platform restores the saved prices, so a round
              // trip is not a change
              const {originalPackage} = state;
              const isSavedCombination =
                template.currency === originalPackage.currency &&
                template.instagram_campaigns === originalPackage.instagram_campaigns &&
                template.tiktok_campaigns === originalPackage.tiktok_campaigns;
              const source = isSavedCombination ? originalPackage : template;

              const isYearly = state.modifiedPackage.package_type === "YEARLY";
              const getPrices = (p: TPackageInformation) => ({
                discounted: (isYearly ? p.price_per_year_with_discount : p.price_per_month_with_discount) ?? 0,
                list: (isYearly ? p.price_per_year : p.price_per_month) ?? 0,
              });
              const getCustomDiscountPercentage = (p: TPackageInformation) => {
                const {discounted, list} = getPrices(p);
                return Math.round((((p.discount ?? 0) - (list - discounted)) / (discounted || 1)) * 100);
              };
              const customDiscountPercentage = getCustomDiscountPercentage(state.modifiedPackage);

              state.modifiedPackage.currency = source.currency;
              state.modifiedPackage.discount_percentage = source.discount_percentage;
              state.modifiedPackage.id = source.id;
              state.modifiedPackage.price = getSubscriptionPrice({
                ...source,
                package_type: state.modifiedPackage.package_type,
              });
              state.modifiedPackage.price_per_month = source.price_per_month;
              state.modifiedPackage.price_per_month_with_discount = source.price_per_month_with_discount;
              state.modifiedPackage.price_per_year = source.price_per_year;
              state.modifiedPackage.price_per_year_with_discount = source.price_per_year_with_discount;

              const next = getPrices(source);
              state.modifiedPackage.discount =
                isSavedCombination && customDiscountPercentage === getCustomDiscountPercentage(originalPackage)
                  ? originalPackage.discount
                  : Math.round(next.list - next.discounted + (next.discounted * customDiscountPercentage) / 100);
              if (isTrial) applyTrialValues(state.modifiedPackage, isCreating);
            },
            false,
            "usePackageAdminStore/applyPackageList",
          );
        },
        changeCurrency: packages => {
          if (packages[0]?.currency === get().modifiedPackage.currency) return;
          get().applyPackageList(packages);
        },
        setInitialPackage: (packages, subscription) => {
          const packageOfUser = packages.find(p => p.name === subscription?.name);
          if (!subscription) {
            const selectedPackage: TPackageInformation = initialPackageState;
            set(
              state => {
                state.originalPackage = selectedPackage;
                state.modifiedPackage = selectedPackage;
              },
              false,
              "usePackageAdminStore/setInitialPackage",
            );
          } else {
            const selectedPackage: TPackageInformation = {
              account_setup_call: false,
              customer_success: false,
              ...subscription!,
              package_category: getSubscriptionCategory(subscription),
              // TODO: temporary fix for the missing template_name field
              ...(subscription?.template_name! === "" && {template_name: "EXPERT"}),
              id: packageOfUser?.id ?? 0,
              discount_percentage: packageOfUser?.discount_percentage ?? 0,
              price_per_month_with_discount:
                packageOfUser?.price_per_month_with_discount ?? subscription?.price_per_month ?? 0,
              price_per_year_with_discount:
                packageOfUser?.price_per_year_with_discount ?? subscription?.price_per_year ?? 0,
              price: getSubscriptionPrice(subscription),
            };

            set(
              state => {
                state.originalPackage = selectedPackage;
                state.modifiedPackage = selectedPackage;
              },
              false,
              "usePackageAdminStore/setInitialPackage",
            );
          }
        },
        changePackage: (packageName, packages, subscription) => {
          const packageOfUser = packages.find(p => p.name === packageName);
          const isCreating = get().originalPackage.name === "NONE";
          const isTrial = !!get().modifiedPackage.admin_assigned_trial;

          set(
            state => {
              if (!packageOfUser) {
                state.modifiedPackage = initialPackageState;
              } else {
                state.modifiedPackage.name = packageName;
                state.modifiedPackage = {
                  ...subscription,
                  ...packageOfUser,
                  package_type: get().modifiedPackage.package_type,
                  valid_from: get().modifiedPackage.valid_from,
                  valid_until: get().modifiedPackage.valid_until,
                  currency: get().modifiedPackage.currency,
                  recurring: get().modifiedPackage.recurring,
                  subscription_email_sent: get().modifiedPackage.subscription_email_sent,
                  price: getSubscriptionPrice({
                    ...packageOfUser,
                    package_type: get().modifiedPackage.package_type,
                  }),
                  buyout: scaleBuyoutForPeriod(packageOfUser?.buyout, get().modifiedPackage.package_type),
                  discount:
                    get().modifiedPackage.package_type === "MONTHLY"
                      ? (packageOfUser?.price_per_month ?? 0) - (packageOfUser?.price_per_month_with_discount ?? 0)
                      : (packageOfUser?.price_per_year ?? 0) - (packageOfUser?.price_per_year_with_discount ?? 0),
                  is_admin_assigned: true,
                };
                delete state.modifiedPackage.template_name;
                // The rebuilt package takes the flag from the saved subscription, which would undo
                // a pending conversion to a paid plan.
                state.modifiedPackage.admin_assigned_trial = isTrial;
                if (isTrial) applyTrialValues(state.modifiedPackage, isCreating);
              }
            },
            false,
            "usePackageAdminStore/changePackage",
          );
        },
        changeValue: (key, value) =>
          set(
            state => {
              const previousPackageType = state.modifiedPackage.package_type;
              state.modifiedPackage[key] = value;
              if (key === "package_type") {
                if (get().modifiedPackage.name !== "ENTERPRISE" && (value === "MONTHLY" || value === "YEARLY"))
                  state.modifiedPackage.price =
                    value === "MONTHLY"
                      ? state.modifiedPackage.price_per_month!
                      : state.modifiedPackage.price_per_year!;
                if (value === "QUARTERLY" || value === "HALF_YEARLY" || value === "THREE_PLUS_TWELVE") {
                  const months = value === "HALF_YEARLY" ? 6 : 3;
                  state.modifiedPackage.price = (state.modifiedPackage.price_per_month ?? 0) * months;
                }
                // Re-derive the stored buyout for the new period from the package's yearly count:
                // the period share for non-standard plans, the full yearly count for monthly/yearly.
                state.modifiedPackage.buyout = scaleBuyoutForPeriod(
                  unscaleBuyoutToYearly(state.modifiedPackage.buyout ?? null, previousPackageType),
                  value as string,
                );
                const durationMonths = BILLING_PERIOD_DURATION_MONTHS[value as TPackagePaymentPeriodType];
                if (durationMonths !== undefined) {
                  state.modifiedPackage.valid_until = dayjs(state.modifiedPackage.valid_from)
                    .add(durationMonths, "month")
                    .toISOString();
                }
                state.modifiedPackage.discount =
                  get().modifiedPackage.package_type === "YEARLY"
                    ? (get().modifiedPackage?.price_per_month ?? 0) -
                      (get().modifiedPackage?.price_per_month_with_discount ?? 0)
                    : (get().modifiedPackage?.price_per_year ?? 0) -
                      (get().modifiedPackage?.price_per_year_with_discount ?? 0);
              }
              if (key === "valid_from") {
                const durationMonths =
                  BILLING_PERIOD_DURATION_MONTHS[state.modifiedPackage.package_type as TPackagePaymentPeriodType];
                if (durationMonths !== undefined) {
                  state.modifiedPackage.valid_until = dayjs(value as string)
                    .add(durationMonths, "month")
                    .toISOString();
                }
              }
            },
            false,
            "usePackageAdminStore/changeValue",
          ),
        cancelChanges: () =>
          set(
            state => {
              state.modifiedPackage = state.originalPackage;
            },
            false,
            "usePackageAdminStore/cancelChanges",
          ),
        saveChanges: async (agency_id, isAgency = false, selectedCurrency, currentSubscription) => {
          const pendingPackage = get().modifiedPackage;

          if (isAgency && pendingPackage.extra_campaign_price === 0) {
            throw new Error();
          }

          if (isAgency && pendingPackage.extra_seat_price === 0) {
            throw new Error();
          }

          if (
            pendingPackage.package_type !== "MONTHLY" &&
            pendingPackage.package_type !== "YEARLY" &&
            !pendingPackage.price &&
            !pendingPackage.admin_assigned_trial
          ) {
            throw new Error();
          }

          set(
            state => {
              if (!state.modifiedPackage.multiple_active_campaigns)
                state.modifiedPackage.number_of_multiple_active_campaigns = 1;
            },
            false,
            "usePackageAdminStore/saveChanges",
          );

          const modifiedPackage = get().modifiedPackage;

          if (get().modifiedPackage.name !== "ENTERPRISE") {
            let packageSubscriptionId = currentSubscription?.id;
            if (get().originalPackage.name === "NONE") {
              // If the user has no subscription, we subsricbe him to one
              const subscribePayload: Record<string, unknown> = {
                currency: selectedCurrency,
                package_type: get().modifiedPackage.package_type,
                agency_id,
              };
              if (get().modifiedPackage.package_type === "THREE_PLUS_TWELVE") {
                subscribePayload.price_per_year = get().modifiedPackage.price_per_year;
              }
              if (get().modifiedPackage.admin_assigned_trial) {
                subscribePayload.admin_assigned_trial = true;
              }
              const newSubscription = await client.post<PackageSubscriptionSerializer>(
                PAYMENT_KEYS.mPackageSubscribe(get().modifiedPackage.id)[0],
                subscribePayload,
              );

              packageSubscriptionId = newSubscription.data.id;
              capturePostHogEvent(PostHogEvents.PackageSubscriptionCreated, {
                agencyId: agency_id,
                subscriptionId: newSubscription.data.id,
              });
            }
            if (
              // If the following values were tweaked, we update the subscription (even if it is a newly created one, since these cannot be set at the point of creation)
              packageSubscriptionId &&
              !isEqual(
                {
                  ...pick(get().modifiedPackage, [
                    "access_to_influencer_network",
                    "admin_assigned_trial",
                    "buyout",
                    "campaign_booster",
                    "content_approval",
                    "currency",
                    "discount",
                    "influencer_level_insights",
                    "influencers_per_campaign",
                    "instagram_campaigns",
                    "invitations_per_campaign",
                    "multiple_active_campaigns",
                    "multiple_brands",
                    "number_of_campaigns",
                    "number_of_multiple_active_campaigns",
                    "number_of_seats",
                    "other_countries",
                    "package_type",
                    "personal_link_stickers",
                    "price",
                    "price_per_year",
                    "pro_creator_mode",
                    "recurring",
                    "short_term_campaigns",
                    "social_feed_filters",
                    "tiktok_campaigns",
                    "tiktok_search",
                    "tiktok_visibility_country_count",
                    "tiktok_visibility_keyword_limit",
                    "tiktok_visibility_sync_interval_hours",
                    "valid_from",
                    "valid_until",
                  ]),
                  currency: selectedCurrency,
                },
                pick(get().originalPackage, [
                  "access_to_influencer_network",
                  "admin_assigned_trial",
                  "buyout",
                  "campaign_booster",
                  "content_approval",
                  "currency",
                  "discount",
                  "influencer_level_insights",
                  "influencers_per_campaign",
                  "instagram_campaigns",
                  "invitations_per_campaign",
                  "multiple_active_campaigns",
                  "multiple_brands",
                  "number_of_campaigns",
                  "number_of_multiple_active_campaigns",
                  "number_of_seats",
                  "other_countries",
                  "package_type",
                  "personal_link_stickers",
                  "price",
                  "price_per_year",
                  "pro_creator_mode",
                  "recurring",
                  "short_term_campaigns",
                  "social_feed_filters",
                  "tiktok_campaigns",
                  "tiktok_search",
                  "tiktok_visibility_country_count",
                  "tiktok_visibility_keyword_limit",
                  "tiktok_visibility_sync_interval_hours",
                  "valid_from",
                  "valid_until",
                ]),
              )
            ) {
              await client.patch(PAYMENT_KEYS.mPackageSubscription(packageSubscriptionId)[0], {
                access_to_influencer_network: get().modifiedPackage.access_to_influencer_network,
                admin_assigned_trial: get().modifiedPackage.admin_assigned_trial ?? false,
                buyout: get().modifiedPackage.buyout,
                campaign_booster: get().modifiedPackage.campaign_booster,
                content_approval: get().modifiedPackage.content_approval,
                currency: selectedCurrency,
                discount: get().modifiedPackage.discount,
                influencer_level_insights: get().modifiedPackage.influencer_level_insights,
                influencers_per_campaign: get().modifiedPackage.influencers_per_campaign,
                instagram_campaigns: get().modifiedPackage.instagram_campaigns,
                invitations_per_campaign: get().modifiedPackage.invitations_per_campaign,
                multiple_active_campaigns: get().modifiedPackage.multiple_active_campaigns,
                multiple_brands: get().modifiedPackage.multiple_brands,
                number_of_campaigns: get().modifiedPackage.number_of_campaigns,
                number_of_multiple_active_campaigns: get().modifiedPackage.number_of_multiple_active_campaigns,
                number_of_seats: get().modifiedPackage.number_of_seats,
                other_countries: get().modifiedPackage.other_countries,
                package_type: get().modifiedPackage.package_type,
                personal_link_stickers: get().modifiedPackage.personal_link_stickers,
                ...getPricePayload(get().modifiedPackage.package_type, get().modifiedPackage.price),
                price_per_year: get().modifiedPackage.price_per_year,
                pro_creator_mode: get().modifiedPackage.pro_creator_mode,
                recurring: get().modifiedPackage.recurring,
                short_term_campaigns: get().modifiedPackage.short_term_campaigns,
                social_feed_filters: get().modifiedPackage.social_feed_filters,
                template_name: get().modifiedPackage.template_name,
                tiktok_campaigns: get().modifiedPackage.tiktok_campaigns,
                tiktok_search: get().modifiedPackage.tiktok_search,
                tiktok_visibility_country_count: get().modifiedPackage.tiktok_visibility_country_count,
                tiktok_visibility_keyword_limit: get().modifiedPackage.tiktok_visibility_keyword_limit,
                tiktok_visibility_sync_interval_hours: get().modifiedPackage.tiktok_visibility_sync_interval_hours,
                valid_from: get().modifiedPackage.valid_from,
                valid_until: get().modifiedPackage.valid_until,
              });
            }
          } else {
            if (get().originalPackage.name !== "ENTERPRISE") {
              // If the user had no enterprise package before, we create a new one - backend now does not support creating an enterprise subscription if there is already one (not necessarily enterprise)
              const subscription = await client.post<PackageSubscriptionSerializer>(
                PAYMENT_KEYS.mPackageSubscriptionList[0],
                {
                  ...modifiedPackage,
                  ...getPricePayload(modifiedPackage.package_type, modifiedPackage.price),
                  agency: agency_id,
                  currency: selectedCurrency,
                  name: "ENTERPRISE",
                  campaign_booster: modifiedPackage.campaign_booster,
                  content_approval: modifiedPackage.content_approval,
                  personal_link_stickers: modifiedPackage.personal_link_stickers,
                  pro_creator_mode: modifiedPackage.pro_creator_mode,
                  short_term_campaigns: modifiedPackage.short_term_campaigns,
                  social_feed_filters: modifiedPackage.social_feed_filters,
                  tiktok_campaigns: modifiedPackage.tiktok_campaigns,
                  tiktok_search: modifiedPackage.tiktok_search,
                },
              );
              capturePostHogEvent(PostHogEvents.PackageSubscriptionCreated, {
                agencyId: agency_id,
                subscriptionId: subscription.data.id,
              });
            } else {
              // If the user had an enterprise package before, we update it
              await client.patch(PAYMENT_KEYS.mPackageSubscription(currentSubscription!.id)[0], {
                ...modifiedPackage,
                ...getPricePayload(modifiedPackage.package_type, modifiedPackage.price),
                currency: selectedCurrency,
              });
            }
          }
          set(
            state => {
              state.modifiedPackage.currency = selectedCurrency;
              state.originalPackage = {...state.modifiedPackage};
            },
            false,
            "usePackageAdminStore/saveChanges",
          );

          return;
        },
        customizePackage: (isAgency = false) =>
          set(
            state => {
              state.modifiedPackage.template_name = state.modifiedPackage.name;
              if (isAgency) {
                state.modifiedPackage.name = "ENTERPRISE";
              }
            },
            false,
            "usePackageAdminStore/customizePackage",
          ),
        cancelSubscription: async (agency_id, cancel_now = false, cancellation_reason) => {
          await client.post(PAYMENT_KEYS.mPackageSubscriptionCancel[0], {
            agency_id,
            cancel_now,
            cancellation_reason: cancellation_reason?.trim() || null,
          });

          if (cancel_now) {
            set(
              state => {
                state.modifiedPackage = initialPackageState;
                state.originalPackage = initialPackageState;
              },
              false,
              "usePackageAdminStore/cancelSubscription",
            );
          }
        },
        reset: () => set(initialState, false, "usePackageAdminStore/reset"),
        setAdminAssignedTrial: (enabled, packages) =>
          set(
            state => {
              if (enabled) {
                if (state.modifiedPackage.name === "ENTERPRISE" && state.modifiedPackage.template_name) {
                  state.modifiedPackage.name = state.modifiedPackage.template_name as TPackageName;
                }
                delete state.modifiedPackage.template_name;
                applyTrialValues(state.modifiedPackage, true);
                return;
              }

              // Leaving the trial (or converting it to a paid plan) restores the list prices
              // and a regular billing period starting today.
              const listPackage = packages.find(p => p.name === state.modifiedPackage.name);
              const packageType = state.modifiedPackage.package_type;
              const durationMonths = BILLING_PERIOD_DURATION_MONTHS[packageType as TPackagePaymentPeriodType];
              const now = dayjs();
              state.modifiedPackage.admin_assigned_trial = false;
              state.modifiedPackage.recurring = true;
              state.modifiedPackage.valid_from = now.toISOString();
              if (durationMonths !== undefined) {
                state.modifiedPackage.valid_until = now.add(durationMonths, "month").toISOString();
              }
              if (listPackage) {
                state.modifiedPackage.discount =
                  packageType === "MONTHLY"
                    ? (listPackage.price_per_month ?? 0) - (listPackage.price_per_month_with_discount ?? 0)
                    : (listPackage.price_per_year ?? 0) - (listPackage.price_per_year_with_discount ?? 0);
                state.modifiedPackage.price = getSubscriptionPrice({...listPackage, package_type: packageType});
                state.modifiedPackage.price_per_month = listPackage.price_per_month;
                state.modifiedPackage.price_per_month_with_discount = listPackage.price_per_month_with_discount;
                state.modifiedPackage.price_per_year = listPackage.price_per_year;
                state.modifiedPackage.price_per_year_with_discount = listPackage.price_per_year_with_discount;
              }
            },
            false,
            "usePackageAdminStore/setAdminAssignedTrial",
          ),
      }),
      {name: "usePackageAdminStore"},
    ),
  ),
);

export default usePackageAdminStore;
